/* eslint-disable react/prop-types, react-hooks/exhaustive-deps */
/**
 * pages/settings/_blocks.jsx — Phase 8C extraction.
 *
 * All inline subcomponents formerly defined at the bottom of Settings.jsx
 * (lines 589-3135 in the pre-split file).  Moved verbatim so behaviour is
 * unchanged; each per-pillar settings page imports the blocks it needs.
 *
 * Do NOT add new settings logic here — this is the legacy compatibility
 * layer.  New settings UI should live in its pillar's page directly.
 */
import { useEffect, useState } from "react";
import {
  Plus, Shield, CalendarCheck, CheckCircle2, X, KeyRound, Lock, Pencil,
  Save, UserCog, Download, Upload, Database, Smartphone, Vibrate,
  Sparkles, Sun, Moon, Monitor, Phone as PhoneIcon, RefreshCw, AlertTriangle,
  Camera, Menu as MenuIcon, Volume2, Mic, Loader2, Activity, MessageSquare,
  ThumbsUp, ThumbsDown, Trash2, ScanLine,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { toast } from "../../components/ui/sonner";
import Modal from "../../components/Modal";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useBranding } from "../../context/TenantBrandingContext";
import InstallPWAButton from "../../components/InstallPWAButton";
import PushSubscriptionsCard from "../../components/PushSubscriptionsCard";
import AvatarUploader from "../../components/AvatarUploader";
import Avatar from "../../components/Avatar";
import { isHapticsEnabled, setHapticsEnabled } from "../../lib/haptics";
import DictTextarea from "../../components/DictTextarea";

function ResetPasswordModal({ target, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (pw.length < 4) {
      setErr("Password must be at least 4 characters.");
      return;
    }
    if (pw !== pw2) {
      setErr("Passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/users/${target.id}/set-password`, { new_password: pw });
      setDone(true);
      setTimeout(() => onSaved(), 1200);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Couldn't reset password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Reset password — ${target.name}`}
      testId="reset-password-modal"
      footer={
        done ? (
          <div className="flex justify-end">
            <button onClick={onClose} className="hv-btn hv-btn-primary" data-testid="reset-password-close">
              Done
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost">
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="hv-btn hv-btn-primary disabled:opacity-60"
              data-testid="reset-password-submit"
            >
              <KeyRound className="w-4 h-4" /> {saving ? "Resetting…" : "Reset password"}
            </button>
          </div>
        )
      }
    >
      {done ? (
        <div className="text-emerald-300 text-sm" data-testid="reset-password-done">
          ✓ Password updated for <span className="font-medium">{target.email}</span>. Share the new password with them
          out-of-band.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="reset-password-form">
          <div className="text-xs text-[#a0aabf]">
            Setting a new password for <span className="text-white font-medium">{target.email}</span>. They will use it
            on their next login.
          </div>
          <input
            type="password"
            placeholder="New password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="hv-input"
            autoComplete="new-password"
            required
            autoFocus
            data-testid="reset-password-input"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            className="hv-input"
            autoComplete="new-password"
            required
            data-testid="reset-password-confirm"
          />
          {err && <div className="text-sm text-rose-300" data-testid="reset-password-error">{err}</div>}
          {/* Hidden submit so Enter key still works */}
          <button type="submit" className="hidden" />
        </form>
      )}
    </Modal>
  );
}


const NAV_KEYS = [
  { key: "dashboard",  label: "Dashboard"  },
  { key: "customers",  label: "Customers"  },
  { key: "leads",      label: "Leads"      },
  { key: "jobcards",   label: "Job Cards"  },
  { key: "tickets",    label: "Tickets"    },
  { key: "scheduler",  label: "Scheduler"  },
  { key: "calendar",   label: "Calendar"   },
  { key: "tasks",      label: "Tasks"      },
  { key: "calls",      label: "Calls"      },
  { key: "settings",   label: "Settings"   },
];

function EditUserModal({ target, actorRole, actorId, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: target.name || "",
    email: target.email || "",
    phone: target.phone || "",
    role: target.role || "technician",
    is_active: target.is_active !== false,
    custom_permissions: Array.isArray(target.custom_permissions) ? [...target.custom_permissions] : [],
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  // Wave 26.10.3 — inline photo + password reset (replaces separate row buttons)
  const [photoUser, setPhotoUser] = useState(target);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwDone, setPwDone] = useState(false);
  const canSetPhoto = target._canSetPhoto !== false;
  const canResetPw = target._canReset !== false;
  const canEditFields = target._canEdit !== false;

  const isSelf = actorId === target.id;
  const isManagerActor = actorRole === "manager";

  const togglePerm = (k) => {
    setForm((f) => ({
      ...f,
      custom_permissions: f.custom_permissions.includes(k)
        ? f.custom_permissions.filter((x) => x !== k)
        : [...f.custom_permissions, k],
    }));
  };

  const submitPw = async () => {
    setPwErr("");
    if (pw.length < 4) {
      setPwErr("Password must be at least 4 characters.");
      return;
    }
    if (pw !== pw2) {
      setPwErr("Passwords don't match.");
      return;
    }
    setPwSaving(true);
    try {
      await api.post(`/users/${target.id}/set-password`, { new_password: pw });
      setPwDone(true);
      setPw("");
      setPw2("");
      setTimeout(() => setPwDone(false), 3000);
    } catch (e2) {
      setPwErr(formatErr(e2.response?.data?.detail) || "Couldn't reset password.");
    } finally {
      setPwSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        role: form.role,
        is_active: form.is_active,
        custom_permissions: form.custom_permissions,
      };
      // Don't try to change own role server-side will reject; gate client-side too
      if (isSelf) delete payload.role;
      await api.put(`/users/${target.id}`, payload);
      setDone(true);
      setTimeout(() => onSaved(), 900);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Couldn't save user.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit user — ${target.name}`}
      testId="edit-user-modal"
      footer={
        done ? null : (
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost" data-testid="edit-user-cancel">
              {canEditFields ? "Cancel" : "Close"}
            </button>
            {canEditFields && (
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="hv-btn hv-btn-primary disabled:opacity-60"
                data-testid="edit-user-submit"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save changes"}
              </button>
            )}
          </div>
        )
      }
    >
      {done ? (
        <div className="text-emerald-300 text-sm" data-testid="edit-user-done">
          ✓ Changes saved for <span className="font-medium">{target.email}</span>.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="edit-user-form">
          {/* Wave 26.10.3 — inline Profile photo section */}
          {canSetPhoto && (
            <div className="hv-card p-3 border-white/5">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-4 h-4 text-[#1fb6c1]" />
                <div className="text-sm font-medium">Profile photo</div>
              </div>
              <AvatarUploader
                user={photoUser}
                targetUid={target.id}
                onChange={(newUrl) => setPhotoUser({ ...photoUser, avatar_url: newUrl })}
              />
            </div>
          )}

          {/* Wave 26.10.3 — inline Reset password section */}
          {canResetPw && (
            <div className="hv-card p-3 border-white/5" data-testid="edit-user-reset-section">
              <div className="flex items-center gap-2 mb-2">
                <KeyRound className="w-4 h-4 text-[#1fb6c1]" />
                <div className="text-sm font-medium">Reset password</div>
              </div>
              <p className="text-xs text-[#647087] mb-2">
                Set a new password for <span className="text-white">{target.email}</span>. They'll use it on next login — share out-of-band.
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                <input
                  type="password"
                  placeholder="New password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="hv-input"
                  autoComplete="new-password"
                  data-testid="edit-user-pw"
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="hv-input"
                  autoComplete="new-password"
                  data-testid="edit-user-pw2"
                />
              </div>
              {pwErr && <div className="text-xs text-rose-300 mt-2" data-testid="edit-user-pw-error">{pwErr}</div>}
              {pwDone && <div className="text-xs text-emerald-300 mt-2" data-testid="edit-user-pw-done">✓ Password updated.</div>}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={submitPw}
                  disabled={pwSaving || !pw}
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid="edit-user-pw-submit"
                >
                  <KeyRound className="w-3 h-3" /> {pwSaving ? "Resetting…" : "Reset password"}
                </button>
              </div>
            </div>
          )}

          {!canEditFields ? (
            <div className="text-xs text-[#647087]">You don't have permission to edit this user's details.</div>
          ) : (
          <>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Name *</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="hv-input mt-1"
                data-testid="edit-user-name"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Email *</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="hv-input mt-1"
                data-testid="edit-user-email"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Phone</label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="hv-input mt-1"
                data-testid="edit-user-phone"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                disabled={isSelf || target.role === "owner"}
                className="hv-input mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="edit-user-role"
              >
                <option value="technician">Technician</option>
                <option value="rep">Rep</option>
                <option value="manager">Manager</option>
                {!isManagerActor && <option value="admin">Admin</option>}
                {/* Owner is only visible if this user is already the owner;
                    it cannot be assigned via the UI (only via admin script). */}
                {target.role === "owner" && <option value="owner">Owner</option>}
                <option value="client">Client</option>
              </select>
              {isSelf && (
                <div className="text-[11px] text-[#647087] mt-1">You can't change your own role.</div>
              )}
              {target.role === "owner" && !isSelf && (
                <div className="text-[11px] text-[#647087] mt-1">Owner role is locked.</div>
              )}
              {isManagerActor && !isSelf && target.role !== "owner" && (
                <div className="text-[11px] text-[#647087] mt-1">Managers cannot promote users to admin.</div>
              )}
            </div>
          </div>

          <div className="hv-card p-3 border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <UserCog className="w-4 h-4 text-[#1fb6c1]" />
              <div className="text-sm font-medium">Custom navigation permissions</div>
            </div>
            <p className="text-xs text-[#647087] mb-3">
              Optional per-user overrides. Leave all unchecked to fall back to the role's default access.
              Each ticked item grants this user access to that section in the nav.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {NAV_KEYS.map((p) => {
                const checked = form.custom_permissions.includes(p.key);
                return (
                  <label
                    key={p.key}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md border cursor-pointer text-sm transition ${
                      checked
                        ? "border-[#1fb6c1]/50 bg-[#1fb6c1]/10 text-white"
                        : "border-white/10 hover:border-white/20 text-[#a0aabf]"
                    }`}
                    data-testid={`edit-user-perm-${p.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePerm(p.key)}
                      className="accent-[#1fb6c1]"
                    />
                    {p.label}
                  </label>
                );
              })}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm" data-testid="edit-user-active-row">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              disabled={isSelf}
              className="accent-emerald-500"
              data-testid="edit-user-active"
            />
            <span className={form.is_active ? "text-emerald-300" : "text-[#a0aabf]"}>
              Account active {form.is_active ? "" : "— user will not be able to log in"}
            </span>
          </label>

          {err && (
            <div className="text-sm text-rose-300" data-testid="edit-user-error">{err}</div>
          )}
          {/* Hidden submit so Enter key still works */}
          <button type="submit" className="hidden" />
          </>
          )}
        </form>
      )}
    </Modal>
  );
}

/* ---------------- Data migration (export / import) ---------------- */
function DataMigrationBlock() {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [report, setReport] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [confirmDb, setConfirmDb] = useState("");
  const [wipe, setWipe] = useState(true);

  const reset = () => { setMsg(""); setErr(""); setReport(null); };

  const handleExport = async () => {
    reset();
    setBusy("export");
    try {
      const { data } = await api.get("/admin/db-export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const a = document.createElement("a");
      a.href = url;
      a.download = `db-${data.source_db || "export"}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const total = Object.values(data.counts || {}).reduce((s, n) => s + n, 0);
      setMsg(`Exported ${total.toLocaleString('en-GB')} documents across ${Object.keys(data.counts || {}).length} collections.`);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Export failed"));
    } finally {
      setBusy("");
    }
  };

  const handleImport = async () => {
    reset();
    if (!importFile) { setErr("Choose a JSON dump file first."); return; }
    if (!confirmDb) { setErr("Type the target DB_NAME to confirm (get it from Settings → Database)."); return; }
    if (!window.confirm(
      `This will ${wipe ? "WIPE every listed collection and replace" : "UPSERT"} the data in the current database (${confirmDb}).\n\nProceed?`
    )) return;
    setBusy("import");
    try {
      const text = await importFile.text();
      const parsed = JSON.parse(text);
      const collections = parsed.collections || parsed;
      const { data } = await api.post("/admin/db-import", {
        collections,
        confirm_target_db: confirmDb,
        wipe_before_insert: wipe,
      });
      setReport(data.report);
      if (data.ok) {
        setMsg(`Import succeeded into ${data.target_db}.`);
      } else {
        setErr(`Import completed with errors — see report.`);
      }
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Import failed") + (e?.message ? ` · ${e.message}` : ""));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="hv-card p-5" data-testid="data-migration-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-[#A78BFA]/15 border border-[#A78BFA]/40 flex items-center justify-center">
          <Database className="w-5 h-5 text-[#A78BFA]" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold">Data migration</h2>
          <p className="text-sm text-[#a0aabf] max-w-2xl mt-1">
            One-shot tools to copy the ENTIRE application database between
            environments. Use on the source (e.g. preview) to download a JSON
            dump; then upload that file in the target (e.g. deployed). Every
            collection is copied verbatim — users, customers, jobs, tickets,
            attachments, schedules, everything.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* EXPORT */}
        <div className="border border-white/10 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2 text-[#1fb6c1] uppercase tracking-wider text-xs font-bold">
            <Download className="w-4 h-4" /> Export (source env)
          </div>
          <p className="text-xs text-[#a0aabf]">
            Download every collection in this environment as a single JSON
            file. Store it somewhere safe — it contains password hashes and
            every customer record.
          </p>
          <button
            onClick={handleExport}
            disabled={busy === "export"}
            className="hv-btn hv-btn-primary w-full"
            data-testid="db-export-btn"
          >
            <Download className="w-4 h-4" /> {busy === "export" ? "Exporting…" : "Download DB dump"}
          </button>
        </div>

        {/* IMPORT */}
        <div className="border border-rose-500/30 bg-rose-500/5 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2 text-rose-300 uppercase tracking-wider text-xs font-bold">
            <Upload className="w-4 h-4" /> Import (target env)
          </div>
          <p className="text-xs text-[#a0aabf]">
            Destructive — replaces every listed collection in the CURRENT
            database. Only use on a freshly provisioned deploy.
          </p>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">JSON dump file</label>
            <input
              type="file"
              accept="application/json,.json"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="hv-input mt-1 text-xs"
              data-testid="db-import-file"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
              Type this env's DB_NAME to confirm
            </label>
            <input
              value={confirmDb}
              onChange={(e) => setConfirmDb(e.target.value)}
              placeholder="e.g. tech_dispatch_55"
              className="hv-input mt-1 font-mono"
              data-testid="db-import-confirm"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-[#a0aabf] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={wipe}
              onChange={(e) => setWipe(e.target.checked)}
              className="accent-[#F43F5E]"
              data-testid="db-import-wipe"
            />
            Wipe each collection before insert (recommended for first migration)
          </label>
          <button
            onClick={handleImport}
            disabled={busy === "import" || !importFile || !confirmDb}
            className="hv-btn w-full !bg-rose-500 !text-white !border-rose-500 hover:!bg-rose-600 disabled:opacity-50"
            data-testid="db-import-btn"
          >
            <Upload className="w-4 h-4" /> {busy === "import" ? "Importing…" : "Restore into this DB"}
          </button>
        </div>
      </div>

      {msg && <div className="mt-3 text-sm text-emerald-300" data-testid="db-migrate-msg">{msg}</div>}
      {err && <div className="mt-3 text-sm text-rose-300" data-testid="db-migrate-err">{err}</div>}
      {report && (
        <pre className="mt-3 p-3 bg-black/30 rounded border border-white/10 text-[11px] overflow-auto max-h-64" data-testid="db-migrate-report">
{JSON.stringify(report, null, 2)}
        </pre>
      )}
    </div>
  );
}



/* ---------------- Password reset requests ---------------- */
function PasswordResetRequestsBlock() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [banner, setBanner] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/password-reset-requests");
      setRequests(data || []);
    } catch (e) {
      /* ignore — endpoint may not exist on older envs */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const pending = requests.filter((r) => r.status === "pending");
  const visible = showResolved ? requests : pending;

  const startApprove = (req) => {
    setApproveTarget(req);
    // Pre-fill with a memorable default so admin can just click Save.
    setNewPw("1234");
    setErr("");
  };

  const doApprove = async () => {
    if (!approveTarget) return;
    // Iter 418.aa — if the user pre-supplied a password (new flow),
    // the override field is OPTIONAL. Only require the min-length
    // check if the admin actually typed something in the override.
    const overrideBytes = (newPw || "").trim();
    if (overrideBytes && overrideBytes.length < 4) {
      setErr("Override password must be at least 4 characters.");
      return;
    }
    if (!overrideBytes && !approveTarget.pending_password_hash) {
      setErr("This request has no user-supplied password — type one in the override field.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/password-reset-requests/${approveTarget.id}/approve`, {
        // Empty body accepts the user's proposed password bytes.
        new_password: overrideBytes || undefined,
      });
      setBanner(
        overrideBytes
          ? `Reset approved for ${approveTarget.email} with your override — share it with them.`
          : `Reset approved for ${approveTarget.email}. They can now log in with the password they typed on the request.`,
      );
      setApproveTarget(null);
      setNewPw("");
      load();
      setTimeout(() => setBanner(""), 6000);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not approve request."));
    } finally {
      setBusy(false);
    }
  };

  const doReject = async (req) => {
    if (!window.confirm(`Reject reset request from ${req.email}?`)) return;
    try {
      await api.post(`/password-reset-requests/${req.id}/reject`);
      load();
    } catch (e) {
      window.alert(formatErr(e?.response?.data?.detail, "Could not reject."));
    }
  };

  if (!loading && requests.length === 0) return null;

  return (
    <div className="hv-card p-5" data-testid="password-reset-requests-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display text-2xl font-bold flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-amber-300" /> Password reset requests
          {pending.length > 0 && (
            <span className="text-[10px] uppercase tracking-wider bg-amber-400/15 text-amber-300 border border-amber-400/40 px-2 py-0.5 rounded-full">
              {pending.length} pending
            </span>
          )}
        </h2>
        <label className="flex items-center gap-2 text-xs text-[#a0aabf] cursor-pointer">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            className="accent-[#1fb6c1]"
            data-testid="password-reset-show-resolved"
          />
          Show resolved
        </label>
      </div>

      {banner && (
        <div className="mb-3 text-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 rounded-md px-3 py-2">
          {banner}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="text-sm text-[#647087] italic">No requests.</div>
      ) : (
        <div className="space-y-2" data-testid="password-reset-list">
          {visible.map((r) => (
            <div
              key={r.id}
              className="border border-white/5 rounded-md px-3 py-2 flex items-start gap-3 flex-wrap"
              data-testid={`password-reset-row-${r.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{r.user_name || r.email}</span>
                  <span className="text-xs font-mono text-[#647087]">{r.email}</span>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                      r.status === "pending"
                        ? "border-amber-400/40 text-amber-300 bg-amber-400/10"
                        : r.status === "approved"
                        ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/10"
                        : "border-rose-400/40 text-rose-300 bg-rose-400/10"
                    }`}
                  >
                    {r.status}
                  </span>
                </div>
                <div className="text-[11px] text-[#647087] mt-0.5">
                  Requested {new Date(r.created_at).toLocaleString('en-GB')}
                  {r.resolved_by_name && (
                    <> · Resolved by {r.resolved_by_name}</>
                  )}
                </div>
                {r.note && (
                  <div className="text-xs text-[#dde3ec] mt-1 whitespace-pre-wrap">
                    "{r.note}"
                  </div>
                )}
              </div>
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <button
                    onClick={() => startApprove(r)}
                    className="hv-btn hv-btn-primary text-xs"
                    data-testid={`password-reset-approve-${r.id}`}
                  >
                    <CheckCircle2 className="w-3 h-3" /> Approve & set password
                  </button>
                  <button
                    onClick={() => doReject(r)}
                    className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:!text-rose-200"
                    data-testid={`password-reset-reject-${r.id}`}
                  >
                    <X className="w-3 h-3" /> Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {approveTarget && (
        <Modal
          open
          onClose={() => setApproveTarget(null)}
          title={`Reset password for ${approveTarget.user_name || approveTarget.email}`}
          testId="password-reset-approve-modal"
          maxWidth="max-w-md"
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              doApprove();
            }}
            className="space-y-3"
          >
            {/* Iter 418.aa — surface the user's REASON so admin can
                verify identity before approving, and clearly flag
                that the user pre-supplied a new password.  */}
            {approveTarget.reason || approveTarget.note ? (
              <div className="rounded-md border border-white/10 bg-white/[0.02] p-2">
                <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">
                  User&apos;s reason
                </div>
                <div className="text-sm text-slate-100 whitespace-pre-wrap">
                  {approveTarget.reason || approveTarget.note}
                </div>
              </div>
            ) : null}
            {approveTarget.pending_password_hash ? (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
                <div className="text-xs font-semibold text-emerald-300 flex items-center gap-1.5">
                  <KeyRound className="w-3 h-3" /> User supplied their own password
                </div>
                <p className="text-[11px] text-[#a0aabf] mt-1">
                  Approve as-is and the user&apos;s typed password becomes their
                  new login password — no need to type anything below. Only
                  fill the override if you want to force a different one.
                </p>
              </div>
            ) : (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
                Old-format request — user didn&apos;t supply a password.
                You&apos;ll need to type one and share it with them.
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                {approveTarget.pending_password_hash ? "Override password (optional)" : "New password"}
              </label>
              <input
                type="text"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="hv-input mt-1 font-mono"
                autoFocus={!approveTarget.pending_password_hash}
                data-testid="password-reset-approve-input"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
              />
              <p className="text-[10px] text-[#647087] mt-1">
                {approveTarget.pending_password_hash
                  ? "Leave blank to accept the user's typed password. Only fill this if you want to override it."
                  : "Share this with the user out-of-band (WhatsApp, phone, etc.)."}
              </p>
            </div>
            {err && (
              <div className="text-sm bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-md px-3 py-2">
                {err}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={busy}
                className="hv-btn hv-btn-primary flex-1"
                data-testid="password-reset-approve-save"
              >
                {busy ? "Saving…" : (newPw ? "Set override password" : "Approve as-is")}
              </button>
              <button
                type="button"
                onClick={() => setApproveTarget(null)}
                className="hv-btn hv-btn-ghost"
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}


/* ---------------- Device & app card ----------------
   Houses everything that's tied to "this device": Install-as-app,
   haptics on/off, and a small "What's new" changelog so users know
   what changed since they last opened the app. */
const CHANGELOG = [
  {
    date: "2026-04-30",
    items: [
      "Install button now lives in Settings — works after the first browser prompt is dismissed.",
      "⌘K command palette: search customers, jobs, tickets and leads from anywhere.",
      "Floating Log-call button moved to the header (gives back the bottom-right corner on mobile).",
      "Recently-viewed chips on the dashboard so you can resume work in one tap.",
      "Pull-to-refresh on mobile lists.",
      "Fixed text fields that erased what you typed when another part of the app polled.",
    ],
  },
  {
    date: "2026-04-28",
    items: [
      "Quotes / Sales orders / Invoices / Statements upload to Cloudflare R2 with a Pay-now link for outstanding invoices.",
      "PDF print no longer renders blank pages at the top — opens a clean popup.",
      "Forgot-my-password flow on the login screen.",
    ],
  },
];

/* ---------------- Session length card -----------------------------------
 * Lets each user choose how long their login stays valid. Stanley's
 * "Token expired" P0 fix — reps on mobile expect their session to
 * survive overnight (or never expire); admins on shared desktops may
 * want shorter. Picks: 1 / 7 / 30 / 90 / 365 days, or "never expire".
 * The backend mints a new JWT with the matching `exp` (or no exp, for
 * never) and updates the cookie + localStorage on save, so the change
 * is live without another login.
 * ----------------------------------------------------------------------- */
const SESSION_OPTIONS = [
  { days: 1,   label: "1 day",     hint: "Best for shared/public devices" },
  { days: 7,   label: "7 days",    hint: "Sign in weekly" },
  { days: 30,  label: "30 days",   hint: "Recommended for mobile PWA" },
  { days: 90,  label: "90 days",   hint: "Quarterly re-auth" },
  { days: 365, label: "1 year",    hint: "Long-running field tablet" },
  { days: 0,   label: "Never",     hint: "Stay signed in until you logout (least secure)" },
];

function SessionLengthCard() {
  const [current, setCurrent] = useState(null);   // server-confirmed value
  const [picked, setPicked] = useState(null);     // optimistic picker value
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/auth/session-preference");
      setCurrent(data.days);
      setPicked(data.days);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Couldn't load session preference."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setErr("");
    setBanner("");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/session-preference", { days: picked });
      // The backend re-mints the JWT — replace the stored token so the
      // next request uses the new (longer) expiry immediately.
      if (data?.token) {
        try { localStorage.setItem("hv_token", data.token); } catch (_) { /* ignore */ }
      }
      setCurrent(data.days);
      setBanner(
        data.days === 0
          ? "Saved. Your session will now stay active until you sign out."
          : `Saved. Your session will stay active for ${data.days} day${data.days === 1 ? "" : "s"} from each sign-in.`,
      );
      setTimeout(() => setBanner(""), 5000);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Couldn't save session preference."));
    } finally {
      setSaving(false);
    }
  };

  const dirty = picked !== current && picked !== null;

  return (
    <div className="hv-card p-5" data-testid="session-length-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#FBBF24]/15 border border-[#FBBF24]/40 flex items-center justify-center">
          <Shield className="w-5 h-5 text-[#FBBF24]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl font-bold">Session length</h2>
          <p className="text-sm text-[#a0aabf] mt-1">
            How long should the app keep you signed in after each login?
            When this runs out, you'll be sent back to the login screen with
            a friendly "session expired" message — no work is lost.
          </p>

          {loading ? (
            <div className="mt-4 text-sm text-[#647087] flex items-center gap-2" data-testid="session-length-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div
                className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2"
                data-testid="session-length-options"
              >
                {SESSION_OPTIONS.map((opt) => {
                  const active = picked === opt.days;
                  return (
                    <button
                      key={opt.days}
                      type="button"
                      onClick={() => setPicked(opt.days)}
                      data-testid={`session-length-option-${opt.days}`}
                      className={`text-left rounded-md border px-3 py-2.5 transition ${
                        active
                          ? "border-[#FBBF24] bg-[#FBBF24]/10 text-white"
                          : "border-white/10 bg-white/[0.02] text-[#dde3ec] hover:border-white/25"
                      }`}
                    >
                      <div className="text-sm font-semibold">{opt.label}</div>
                      <div className="text-[11px] text-[#a0aabf] mt-0.5 leading-tight">
                        {opt.hint}
                      </div>
                    </button>
                  );
                })}
              </div>

              {picked === 0 && (
                <div
                  className="mt-3 text-xs text-amber-200/90 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2"
                  data-testid="session-length-never-warning"
                >
                  Heads up: a non-expiring session means anyone who picks up
                  this device can use the app as you. Only choose this on
                  devices you control personally.
                </div>
              )}

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={save}
                  disabled={!dirty || saving}
                  className="hv-btn hv-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="session-length-save"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "Saving…" : "Save session length"}
                </button>
                {!dirty && current !== null && (
                  <span className="text-xs text-[#647087]" data-testid="session-length-current">
                    Current: <span className="text-[#dde3ec] font-medium">
                      {current === 0 ? "Never expires" : `${current} day${current === 1 ? "" : "s"}`}
                    </span>
                  </span>
                )}
                {err && (
                  <span className="text-xs text-rose-300" data-testid="session-length-error">{err}</span>
                )}
                {banner && (
                  <span className="text-xs text-emerald-300" data-testid="session-length-success">{banner}</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function DeviceAppCard() {
  const { branding } = useBranding();
  const BRAND_NAME = branding?.app_name || "Rogue";
  const [haptics, setHaptics] = useState(isHapticsEnabled());
  const isMobile = typeof window !== "undefined" && /Mobi|Android|iPhone|iPad/.test(navigator.userAgent);
  const toggleHaptics = () => {
    const next = !haptics;
    setHaptics(next);
    setHapticsEnabled(next);
    if (next && navigator.vibrate) navigator.vibrate(15);
  };
  return (
    <div className="hv-card p-5" data-testid="device-app-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1fb6c1]/15 border border-[#1fb6c1]/40 flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5 text-[#1fb6c1]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl font-bold">Device & app</h2>
          <p className="text-sm text-[#a0aabf] mt-1">
            Install the app on this device, fine-tune phone behaviour, and see what's changed.
          </p>

          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <div className="border border-white/10 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">
                Install
              </div>
              <p className="text-xs text-[#a0aabf] mb-2 leading-relaxed">
                Adds {BRAND_NAME} to your home screen so it opens like a native app
                — works offline and pushes notifications.
              </p>
              <InstallPWAButton variant="primary" />
            </div>

            <ThemeTogglePanel />

            <div className="border border-white/10 rounded-md p-3 flex items-start gap-3">
              <Vibrate className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">Haptic feedback{!isMobile && " (phone only)"}</div>
                <p className="text-[11px] text-[#647087] leading-relaxed mt-0.5">
                  Tiny vibrations on important taps so you know they registered. Android only.
                </p>
              </div>
              <button
                type="button"
                onClick={toggleHaptics}
                role="switch"
                aria-checked={haptics}
                data-testid="haptics-toggle"
                className={`relative w-10 h-5 rounded-full border transition shrink-0 ${
                  haptics
                    ? "bg-[#1fb6c1]/40 border-[#1fb6c1]/60"
                    : "bg-white/5 border-white/10"
                }`}
              >
                <span
                  className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition ${
                    haptics ? "left-5" : "left-0.5"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="mt-5">
            <PushSubscriptionsCard />
          </div>

          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3 h-3" /> What's new
            </div>
            <div className="space-y-3">
              {CHANGELOG.map((entry) => (
                <div
                  key={entry.date}
                  className="border border-white/5 rounded-md p-3 bg-white/[0.02]"
                  data-testid={`changelog-${entry.date}`}
                >
                  <div className="text-[11px] font-mono text-[#1fb6c1] mb-1">{entry.date}</div>
                  <ul className="text-xs text-[#dde3ec] space-y-1 leading-relaxed list-disc list-outside ml-4">
                    {entry.items.map((it, idx) => (
                      <li key={idx}>{it}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ---------------- Asset manager (vehicles) ----------------
   Manager / admin / owner only. Vehicles seeded by backend on first run
   (Ox / Panda / Blitz). Edit specs here so the JC editor's vehicle picker
   shows useful info + colour-codes the calendar. */
function AssetManagerBlock() {
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null); // either a draft object or existing
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/vehicles");
      setVehicles(data || []);
    } catch (_) {
      /* silent */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (form) => {
    if (form.id) {
      await api.put(`/vehicles/${form.id}`, form);
    } else {
      await api.post("/vehicles", form);
    }
    setEditing(null);
    setCreating(false);
    load();
  };

  const remove = async (vid) => {
    if (!window.confirm("Delete this vehicle from the fleet?")) return;
    await api.delete(`/vehicles/${vid}`);
    load();
  };

  return (
    <div className="hv-card p-5" data-testid="asset-manager-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display text-2xl font-bold flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-[#1fb6c1]" /> Asset manager — fleet
        </h2>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="hv-btn hv-btn-primary text-xs"
          data-testid="asset-manager-add"
        >
          <Plus className="w-3.5 h-3.5" /> Add vehicle
        </button>
      </div>
      <p className="text-sm text-[#a0aabf] mb-3">
        Vehicles available to assign on job cards. Conflicts (same vehicle,
        overlapping times) are flagged in the JC editor.
      </p>
      {vehicles.length === 0 ? (
        <div className="text-sm text-[#647087] italic">No vehicles yet.</div>
      ) : (
        <ul className="space-y-1.5" data-testid="asset-manager-list">
          {vehicles.map((v) => (
            <li
              key={v.id}
              className="flex items-center gap-3 border border-white/5 rounded-md px-3 py-2 hover:bg-white/[0.02] flex-wrap"
              data-testid={`asset-manager-row-${v.id}`}
            >
              <span
                className="w-3 h-3 rounded-full border border-white/20"
                style={{ background: v.color || "#1fb6c1" }}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold flex items-center gap-2">
                  {v.name}
                  {v.registration && (
                    <span className="text-[10px] font-mono text-[#647087]">
                      {v.registration}
                    </span>
                  )}
                  {!v.is_active && (
                    <span className="text-[9px] uppercase tracking-wider text-rose-300 border border-rose-400/40 rounded px-1.5">
                      inactive
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[#647087] flex flex-wrap gap-x-3 gap-y-0.5">
                  {v.make && <span>{v.make}</span>}
                  {v.model && <span>· {v.model}</span>}
                  {v.year && <span>· {v.year}</span>}
                  {v.fuel_type && <span>· {v.fuel_type}</span>}
                  {v.odometer_km != null && <span>· {Number(v.odometer_km).toLocaleString('en-GB')} km</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditing(v)}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid={`asset-manager-edit-${v.id}`}
              >
                <Pencil className="w-3 h-3" /> Edit
              </button>
              <button
                type="button"
                onClick={() => remove(v.id)}
                className="hv-btn hv-btn-ghost text-xs text-rose-300"
                data-testid={`asset-manager-delete-${v.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {(editing || creating) && (
        <VehicleEditorModal
          initial={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSave={save}
        />
      )}
    </div>
  );
}

function VehicleEditorModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(
    initial || {
      name: "",
      registration: "",
      make: "",
      model: "",
      year: "",
      fuel_type: "",
      odometer_km: "",
      vin: "",
      color: "#1FB6C1",
      // Iter 418.bm (Dirk, 2026-02-28) — paint colour + vehicle type
      // so the JC allocation dropdown can render "Ford Ranger · CA123·456 · White · Bakkie".
      vehicle_color: "",
      vehicle_type: "",
      is_active: true,
      notes: "",
    },
  );
  const setField = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? `Edit ${initial.name}` : "Add vehicle"}
      testId="asset-manager-modal"
      maxWidth="max-w-md"
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSave({
            ...form,
            year: form.year ? Number(form.year) : null,
            odometer_km: form.odometer_km ? Number(form.odometer_km) : null,
          });
        }}
      >
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Name *</label>
          <input
            required
            value={form.name}
            onChange={setField("name")}
            className="hv-input mt-1"
            data-testid="asset-manager-input-name"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Registration</label>
            <input value={form.registration} onChange={setField("registration")} className="hv-input mt-1 font-mono" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Calendar colour</label>
            <input
              type="color"
              value={form.color || "#1FB6C1"}
              onChange={setField("color")}
              className="hv-input mt-1 h-10 p-1"
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Make</label>
            <input value={form.make} onChange={setField("make")} className="hv-input mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Model</label>
            <input value={form.model} onChange={setField("model")} className="hv-input mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Year</label>
            <input type="number" value={form.year || ""} onChange={setField("year")} className="hv-input mt-1" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Fuel</label>
            <select value={form.fuel_type || ""} onChange={setField("fuel_type")} className="hv-input mt-1">
              <option value="">—</option>
              <option value="diesel">Diesel</option>
              <option value="petrol">Petrol</option>
              <option value="hybrid">Hybrid</option>
              <option value="ev">Electric</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Odometer (km)</label>
            <input type="number" value={form.odometer_km || ""} onChange={setField("odometer_km")} className="hv-input mt-1" />
          </div>
        </div>
        {/* Iter 418.bm (Dirk, 2026-02-28) — paint colour + type. */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Colour</label>
            <input
              value={form.vehicle_color || ""}
              onChange={setField("vehicle_color")}
              placeholder="e.g. White, Silver, Red"
              className="hv-input mt-1"
              data-testid="asset-manager-input-vehicle-color"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Type</label>
            <select
              value={form.vehicle_type || ""}
              onChange={setField("vehicle_type")}
              className="hv-input mt-1"
              data-testid="asset-manager-input-vehicle-type"
            >
              <option value="">—</option>
              <option value="car">Car</option>
              <option value="bakkie">Bakkie</option>
              <option value="van">Van</option>
              <option value="truck">Truck</option>
              <option value="suv">SUV</option>
              <option value="trailer">Trailer</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">VIN</label>
          <input value={form.vin || ""} onChange={setField("vin")} className="hv-input mt-1 font-mono" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Notes</label>
          <DictTextarea value={form.notes || ""} onChange={setField("notes")} className="hv-input mt-1 min-h-[60px]" />
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!form.is_active} onChange={setField("is_active")} className="accent-[#1fb6c1]" />
          Active in fleet (uncheck to hide from JC pickers)
        </label>
        <div className="flex gap-2 pt-2 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost flex-1">
            Cancel
          </button>
          <button type="submit" className="hv-btn hv-btn-primary flex-1" data-testid="asset-manager-save">
            <Save className="w-3.5 h-3.5" /> Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================================
 * Theme picker panel — 3-way (Auto / Light / Dark)
 * Iter 231.8 (2026-05-26) — "auto" follows the device's
 * prefers-color-scheme. It's the new default for first-time visitors.
 * ========================================================================== */
function ThemeTogglePanel() {
  const { theme, mode, setMode } = useTheme();
  const ResolvedIcon = theme === "dark" ? Moon : Sun;
  const resolvedColor = theme === "dark" ? "text-violet-300" : "text-amber-400";
  return (
    <div className="border border-white/10 rounded-md p-3 flex items-start gap-3" data-testid="theme-panel">
      {mode === "auto" ? (
        <Monitor className="w-4 h-4 text-[#1fb6c1] shrink-0 mt-0.5" data-testid="theme-icon-auto" />
      ) : (
        <ResolvedIcon className={`w-4 h-4 ${resolvedColor} shrink-0 mt-0.5`} data-testid={`theme-icon-${mode}`} />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">Appearance</div>
        <p className="text-[11px] text-[#647087] leading-relaxed mt-0.5 mb-2">
          {mode === "auto"
            ? `Following your device — currently ${theme}.`
            : "Force a fixed theme. Saved per device."}
        </p>
        <div className="inline-flex rounded-md border border-white/10 overflow-hidden" data-testid="theme-toggle">
          <button
            type="button"
            onClick={() => setMode("auto")}
            className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition ${
              mode === "auto"
                ? "bg-[#1fb6c1] text-black font-bold"
                : "text-[#a0aabf] hover:bg-white/5"
            }`}
            data-testid="theme-auto"
            title="Match my device's light / dark setting"
          >
            <Monitor className="w-3.5 h-3.5" /> Auto
          </button>
          <button
            type="button"
            onClick={() => setMode("light")}
            className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition ${
              mode === "light"
                ? "bg-[#1fb6c1] text-black font-bold"
                : "text-[#a0aabf] hover:bg-white/5"
            }`}
            data-testid="theme-light"
          >
            <Sun className="w-3.5 h-3.5" /> Light
          </button>
          <button
            type="button"
            onClick={() => setMode("dark")}
            className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition ${
              mode === "dark"
                ? "bg-[#1fb6c1] text-black font-bold"
                : "text-[#a0aabf] hover:bg-white/5"
            }`}
            data-testid="theme-dark"
          >
            <Moon className="w-3.5 h-3.5" /> Dark
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * Phone normaliser block — Wave 6
 * Dry-run → review diff → commit. Admin only.
 * ========================================================================== */
function PhoneNormaliserBlock() {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [committed, setCommitted] = useState(null);

  const dryRun = async () => {
    setBusy(true);
    setErr("");
    setCommitted(null);
    try {
      const r = await api.post("/admin/phones/normalise", { dry_run: true });
      setReport(r.data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not run dry-run"));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/admin/phones/normalise", { dry_run: false });
      setCommitted(r.data);
      setReport(null);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not commit"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hv-card p-5" data-testid="phone-normaliser-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
          <PhoneIcon className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl font-bold">Phone number cleanup</h2>
          <p className="text-sm text-[#a0aabf] mt-1">
            Normalises every phone number in the system to E.164 format
            (<span className="font-mono text-[#1fb6c1]">+27…</span> for SA, otherwise preserved as international).
            Run a dry-run first to preview changes — then commit when you're happy.
          </p>

          {err && (
            <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2 mt-3">
              {err}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              onClick={dryRun}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="phone-norm-dryrun"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
              {busy && !committed ? "Scanning…" : "Run dry-run"}
            </button>
            {report && report.proposed?.length > 0 && (
              <button
                type="button"
                onClick={commit}
                disabled={busy}
                className="hv-btn hv-btn-primary text-xs"
                data-testid="phone-norm-commit"
              >
                <Save className="w-3.5 h-3.5" /> Commit {report.proposed.length} change(s)
              </button>
            )}
          </div>

          {committed && (
            <div
              className="mt-4 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2"
              data-testid="phone-norm-committed"
            >
              <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5" />
              Updated {committed.updated} record(s) across {Object.keys(committed.by_collection || {}).length} collections.
            </div>
          )}

          {report && (
            <div className="mt-4 space-y-2" data-testid="phone-norm-report">
              <div className="text-[10px] uppercase tracking-wider text-[#647087]">
                Scanned <b className="text-white">{report.scanned}</b> phone fields ·{" "}
                <span className="text-emerald-300">{report.already_clean}</span> already clean ·{" "}
                <span className="text-amber-300">{report.proposed?.length || 0}</span> would change ·{" "}
                <span className="text-rose-300">{report.skipped || 0}</span> skipped
              </div>
              {report.proposed?.length === 0 ? (
                <div className="text-xs text-emerald-300 italic">All phone numbers already clean.</div>
              ) : (
                <div className="border border-white/10 rounded-md overflow-hidden max-h-72 overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-white/5 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1 text-[#647087] uppercase">Where</th>
                        <th className="text-left px-2 py-1 text-[#647087] uppercase">Before</th>
                        <th className="text-left px-2 py-1 text-[#647087] uppercase">After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.proposed.slice(0, 200).map((p, i) => (
                        <tr key={i} className="border-t border-white/5" data-testid={`phone-norm-row-${i}`}>
                          <td className="px-2 py-1 text-[#647087] font-mono">
                            {p.collection}.{p.field}
                            {p.label ? <span className="text-[#a0aabf] ml-1">· {p.label}</span> : null}
                          </td>
                          <td className="px-2 py-1 font-mono text-rose-300">{p.before || "—"}</td>
                          <td className="px-2 py-1 font-mono text-emerald-300">{p.after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {report.proposed.length > 200 && (
                    <div className="text-[10px] text-[#647087] text-center py-1">
                      …and {report.proposed.length - 200} more rows.
                    </div>
                  )}
                </div>
              )}
              <div className="text-[10px] text-[#647087] flex items-start gap-1.5">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                Numbers already starting with <span className="font-mono">+</span>,{" "}
                <span className="font-mono">00</span>, or that look international are preserved unchanged.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Menu customisation card ----------------
   Lets a user toggle which sidebar items live in the primary nav vs the
   "More" foldable group. Persists to localStorage and broadcasts a
   `hv:nav:hidden:changed` event so the Layout updates instantly. */
const MENU_ITEMS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "calendar", label: "Calendar" },
  { key: "inbox", label: "Inbox (daily briefs)" },
  { key: "tasks", label: "Tasks" },
  { key: "calls", label: "Call log" },
  { key: "customers", label: "Customers" },
  { key: "leads", label: "Leads" },
  { key: "followups", label: "Follow-ups" },
  { key: "jobcards", label: "Job Cards" },
  { key: "tickets", label: "Support Tickets" },
  { key: "scheduler", label: "Service Schedule" },
  { key: "orders", label: "Order requests" },
];
const DEFAULT_HIDDEN = ["calls", "scheduler", "followups", "orders"];

// Tools children — sub-menu items the user can also drag between
// "Under Tools" (default), "Main nav" (promote to top-level), or "More".
// MUST stay in sync with TOOLS_CHILDREN in Layout.jsx.
const TOOLS_SUBITEMS = [
  { key: "tool.equipment", label: "Equipment & Inventory" },
  { key: "tool.engineering_desk", label: "Engineering Desk" },
  { key: "tool.gallery", label: "Gallery" },
  { key: "tool.suppliers", label: "Suppliers" },
  { key: "tool.onboarding", label: "Lead Onboarding" },
  { key: "tool.account_applications", label: "Account Application" },
  { key: "tool.trips", label: "Plan a trip" },
  { key: "tool.analytics", label: "Analytics" },
  { key: "tool.ocr", label: "Invoice OCR" },
  { key: "tool.account_app_public", label: "Public Account Application link" },
  { key: "tool.users_roles", label: "Users & Roles" },
  { key: "tool.bug_reports", label: "Bug reports" },
];
// A tool sub-item is "promoted to main" if listed here; "demoted to more" if
// listed in `hidden`; otherwise stays under Tools. Mutually exclusive.
const DEFAULT_PROMOTED = [];

function MenuCustomisationCard() {
  // Primary-nav item visibility (existing behaviour — toggle Main ↔ More)
  const [hidden, setHidden] = useState(() => {
    try {
      const saved = localStorage.getItem("hv_nav_hidden");
      if (saved) return new Set(JSON.parse(saved));
    } catch (_e) { /* ignore */ }
    return new Set(DEFAULT_HIDDEN);
  });
  // Tool sub-items promoted to top-level
  const [promotedTools, setPromotedTools] = useState(() => {
    try {
      const saved = localStorage.getItem("hv_tools_promoted");
      if (saved) return new Set(JSON.parse(saved));
    } catch (_e) { /* ignore */ }
    return new Set(DEFAULT_PROMOTED);
  });
  // Tool sub-items demoted to More
  const [demotedTools, setDemotedTools] = useState(() => {
    try {
      const saved = localStorage.getItem("hv_tools_demoted");
      if (saved) return new Set(JSON.parse(saved));
    } catch (_e) { /* ignore */ }
    return new Set();
  });

  const persist = (nextHidden, nextPromoted, nextDemoted) => {
    setHidden(nextHidden);
    setPromotedTools(nextPromoted);
    setDemotedTools(nextDemoted);
    try {
      localStorage.setItem("hv_nav_hidden", JSON.stringify([...nextHidden]));
      localStorage.setItem("hv_tools_promoted", JSON.stringify([...nextPromoted]));
      localStorage.setItem("hv_tools_demoted", JSON.stringify([...nextDemoted]));
    } catch (_e) { /* ignore */ }
    window.dispatchEvent(new CustomEvent("hv:nav:hidden:changed", {
      detail: { hidden: [...nextHidden], toolsPromoted: [...nextPromoted], toolsDemoted: [...nextDemoted] },
    }));
    // Also push to server for cross-device sync
    api.patch("/users/me/preferences", {
      nav_hidden: [...nextHidden],
      tools_promoted: [...nextPromoted],
      tools_demoted: [...nextDemoted],
    }).catch(() => {});
  };

  const moveItem = (key, target) => {
    // target ∈ { "main", "more" } (for top-level items)
    const nh = new Set(hidden);
    if (target === "more") nh.add(key);
    else nh.delete(key);
    persist(nh, promotedTools, demotedTools);
  };

  const moveTool = (key, target) => {
    // target ∈ { "main", "tools", "more" }
    const nPr = new Set(promotedTools);
    const nDe = new Set(demotedTools);
    nPr.delete(key);
    nDe.delete(key);
    if (target === "main") nPr.add(key);
    else if (target === "more") nDe.add(key);
    // tools = default → not in either set
    persist(hidden, nPr, nDe);
  };

  const reset = () => persist(new Set(DEFAULT_HIDDEN), new Set(), new Set());

  // Groupings
  const mainItems = MENU_ITEMS.filter((it) => !hidden.has(it.key));
  const moreItems = MENU_ITEMS.filter((it) => hidden.has(it.key));
  const promotedToolsItems = TOOLS_SUBITEMS.filter((it) => promotedTools.has(it.key));
  const demotedToolsItems = TOOLS_SUBITEMS.filter((it) => demotedTools.has(it.key));
  const defaultToolsItems = TOOLS_SUBITEMS.filter(
    (it) => !promotedTools.has(it.key) && !demotedTools.has(it.key),
  );

  return (
    <div className="hv-card p-5" data-testid="menu-customisation-card" id="menu-customisation">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 flex items-center justify-center shrink-0">
          <MenuIcon className="w-5 h-5 text-[#1FB6C1]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-display text-2xl font-bold">Menu customisation</h2>
            <button type="button" onClick={reset} className="hv-btn hv-btn-ghost text-[10px]" data-testid="menu-reset">
              <RefreshCw className="w-3 h-3" /> Reset
            </button>
          </div>
          <p className="text-sm text-[#a0aabf] mt-1">
            Move any nav item between <strong>Main nav</strong> (always visible in the sidebar) and <strong>More</strong> (tucked under the More tile). Permissions still apply — items you can't access are hidden regardless.
          </p>

          {/* TOP-LEVEL ITEMS */}
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <MenuBucket title="Main nav" accent="#1FB6C1" count={mainItems.length} testId="bucket-main">
              {mainItems.map((it) => (
                <BucketRow key={it.key} label={it.label} testId={`menu-item-${it.key}`}>
                  <button type="button" onClick={() => moveItem(it.key, "more")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`menu-move-${it.key}-more`}>
                    Move to More →
                  </button>
                </BucketRow>
              ))}
              {mainItems.length === 0 && <EmptyHint>All items moved to More.</EmptyHint>}
            </MenuBucket>
            <MenuBucket title="More" accent="#A78BFA" count={moreItems.length} testId="bucket-more">
              {moreItems.map((it) => (
                <BucketRow key={it.key} label={it.label} testId={`menu-item-${it.key}`}>
                  <button type="button" onClick={() => moveItem(it.key, "main")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`menu-move-${it.key}-main`}>
                    ← Promote to Main
                  </button>
                </BucketRow>
              ))}
              {moreItems.length === 0 && <EmptyHint>Nothing demoted yet.</EmptyHint>}
            </MenuBucket>
          </div>

          {/* TOOLS SUB-ITEMS */}
          <div className="mt-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#647087] mb-2">Tools sub-items</div>
            <div className="text-xs text-[#a0aabf] mb-3">
              Each tool can stay under <strong>Tools</strong> (default), be promoted to <strong>Main nav</strong> as a top-level item, or hidden inside <strong>More</strong>.
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <MenuBucket title="Main nav" accent="#1FB6C1" count={promotedToolsItems.length} testId="bucket-tools-main">
                {promotedToolsItems.map((it) => (
                  <BucketRow key={it.key} label={it.label} testId={`tool-${it.key}`}>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => moveTool(it.key, "tools")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`tool-move-${it.key}-tools`}>→ Tools</button>
                      <button type="button" onClick={() => moveTool(it.key, "more")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`tool-move-${it.key}-more`}>→ More</button>
                    </div>
                  </BucketRow>
                ))}
                {promotedToolsItems.length === 0 && <EmptyHint>No tools promoted.</EmptyHint>}
              </MenuBucket>
              <MenuBucket title="Under Tools" accent="#F59E0B" count={defaultToolsItems.length} testId="bucket-tools-default">
                {defaultToolsItems.map((it) => (
                  <BucketRow key={it.key} label={it.label} testId={`tool-${it.key}`}>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => moveTool(it.key, "main")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`tool-move-${it.key}-main`}>← Main</button>
                      <button type="button" onClick={() => moveTool(it.key, "more")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`tool-move-${it.key}-more`}>→ More</button>
                    </div>
                  </BucketRow>
                ))}
                {defaultToolsItems.length === 0 && <EmptyHint>All tools re-bucketed.</EmptyHint>}
              </MenuBucket>
              <MenuBucket title="More" accent="#A78BFA" count={demotedToolsItems.length} testId="bucket-tools-more">
                {demotedToolsItems.map((it) => (
                  <BucketRow key={it.key} label={it.label} testId={`tool-${it.key}`}>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => moveTool(it.key, "main")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`tool-move-${it.key}-main`}>← Main</button>
                      <button type="button" onClick={() => moveTool(it.key, "tools")} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`tool-move-${it.key}-tools`}>← Tools</button>
                    </div>
                  </BucketRow>
                ))}
                {demotedToolsItems.length === 0 && <EmptyHint>No tools hidden.</EmptyHint>}
              </MenuBucket>
            </div>
          </div>

          <div className="text-[10px] text-[#647087] mt-3">
            Changes save instantly and sync across your devices. Items you don't have permission for are always hidden automatically.
          </div>
        </div>
      </div>
    </div>
  );
}

function MenuBucket({ title, accent, count, children, testId }) {
  return (
    <div className="rounded-md border p-2" style={{ borderColor: `${accent}40`, background: `${accent}08` }} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>
          {title}
        </span>
        <span className="text-[10px] font-mono" style={{ color: accent }}>{count}</span>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function BucketRow({ label, children, testId }) {
  return (
    <div className="flex items-center justify-between gap-2 bg-black/30 rounded px-2 py-1.5" data-testid={testId}>
      <span className="text-xs truncate flex-1">{label}</span>
      {children}
    </div>
  );
}
function EmptyHint({ children }) {
  return <div className="text-[10px] text-[#647087] italic px-2 py-2">{children}</div>;
}


/* ---------------- Hidro preferences card ----------------
   Voice (TTS voice picker), Daily brief delivery time. */
const TTS_VOICES = [
  { id: "alloy", label: "Alloy (default, neutral)" },
  { id: "echo", label: "Echo (deeper)" },
  { id: "fable", label: "Fable (warm)" },
  { id: "onyx", label: "Onyx (rich)" },
  { id: "nova", label: "Nova (bright)" },
  { id: "shimmer", label: "Shimmer (soft)" },
];

function HidroPreferencesCard() {
  const [voice, setVoice] = useState(() => localStorage.getItem("hidro_voice") || "alloy");
  const [briefSettings, setBriefSettings] = useState(null);
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    api.get("/hidro/brief/settings").then(({ data }) => setBriefSettings(data)).catch(() => {});
  }, []);

  const saveVoice = (v) => {
    setVoice(v);
    try { localStorage.setItem("hidro_voice", v); } catch (_e) { /* noop */ }
  };

  const saveBrief = async (next) => {
    try {
      const { data } = await api.put("/hidro/brief/settings", next);
      setBriefSettings(data);
      setSavedAt(Date.now());
    } catch (_e) { /* noop */ }
  };

  const previewVoice = async () => {
    try {
      const resp = await api.post("/hidro/voice/speak", { text: `Hi, this is Hidro speaking in the ${voice} voice.` }, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const audio = new Audio(url);
      audio.play();
    } catch (_e) { /* noop */ }
  };

  return (
    <div className="hv-card p-5" data-testid="hidro-prefs-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#A78BFA]/15 border border-[#A78BFA]/40 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-[#A78BFA]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl font-bold">Hidro AI</h2>
          <p className="text-sm text-[#a0aabf] mt-1">Voice + daily brief preferences.</p>

          <div className="mt-4 grid md:grid-cols-2 gap-3">
            {/* Voice */}
            <div className="border border-white/10 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1 flex items-center gap-1.5"><Volume2 className="w-3 h-3" /> Speech voice</div>
              <select
                value={voice}
                onChange={(e) => saveVoice(e.target.value)}
                className="hv-input text-sm w-full"
                data-testid="hidro-voice-select"
              >
                {TTS_VOICES.map((v) => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={previewVoice}
                className="hv-btn hv-btn-ghost text-xs mt-2"
                data-testid="hidro-voice-preview"
              >
                <Mic className="w-3 h-3" /> Preview voice
              </button>
            </div>

            {/* Daily brief */}
            <div className="border border-white/10 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Daily brief (SAST)</div>
              {briefSettings ? (
                <>
                  <label className="flex items-center gap-2 text-sm mb-2">
                    <input
                      type="checkbox"
                      checked={!!briefSettings.brief_enabled}
                      onChange={(e) => saveBrief({ brief_enabled: e.target.checked })}
                      className="accent-[#1FB6C1]"
                      data-testid="hidro-brief-enabled"
                    />
                    <span>Auto-deliver each morning</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs">
                      <span className="text-[#647087] block mb-0.5">Morning</span>
                      <input
                        type="time"
                        value={briefSettings.brief_time || "06:00"}
                        onChange={(e) => saveBrief({ brief_time: e.target.value })}
                        className="hv-input text-sm w-full"
                        data-testid="hidro-brief-time"
                      />
                    </label>
                    <label className="text-xs">
                      <span className="text-[#647087] block mb-0.5">Evening recap</span>
                      <input
                        type="time"
                        value={briefSettings.recap_time || "18:00"}
                        onChange={(e) => saveBrief({ recap_time: e.target.value })}
                        className="hv-input text-sm w-full"
                        data-testid="hidro-recap-time"
                      />
                    </label>
                  </div>
                  <div className="text-[10px] text-[#647087] mt-1.5">
                    Timezone: Africa/Johannesburg.
                    {savedAt > 0 && Date.now() - savedAt < 4000 && <span className="text-emerald-300 ml-2">Saved.</span>}
                  </div>
                </>
              ) : (
                <div className="text-xs text-[#647087]">Loading…</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ---------------- Brief coaching (Wave 27.5) ----------------
   Persistent standing-instructions textarea + recent feedback log.
   The instructions are injected into every daily-brief LLM call as
   binding directives. Per-brief thumbs-up / down feedback still
   appears under each brief in /inbox and is layered on top of these
   instructions. */
function BriefCoachingCard() {
  const [instructions, setInstructions] = useState("");
  const [initial, setInitial] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/hidro/brief/coaching");
      setInstructions(data.instructions || "");
      setInitial(data.instructions || "");
      setUpdatedAt(data.updated_at || null);
      setRecent(data.recent_feedback || []);
    } catch (_e) { /* noop */ }
    finally { setLoaded(true); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // If the user lands on /settings#brief-coaching, scroll the card into view.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#brief-coaching") return;
    const t = setTimeout(() => {
      document.getElementById("brief-coaching")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [loaded]);

  const dirty = instructions.trim() !== (initial || "").trim();

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/hidro/brief/coaching", { instructions });
      setInitial(data.instructions || "");
      setUpdatedAt(new Date().toISOString());
      setSavedAt(Date.now());
    } catch (_e) { /* noop */ }
    finally { setBusy(false); }
  };

  const deleteFeedback = async (fid) => {
    try {
      await api.delete(`/hidro/brief/coaching/feedback/${fid}`);
      setRecent((arr) => arr.filter((r) => r.id !== fid));
    } catch (_e) { /* noop */ }
  };

  return (
    <div id="brief-coaching" className="hv-card p-5" data-testid="brief-coaching-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 flex items-center justify-center shrink-0">
          <MessageSquare className="w-5 h-5 text-[#1FB6C1]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl font-bold">Coach Hidro — brief instructions</h2>
          <p className="text-sm text-[#a0aabf] mt-1">
            Tell Hidro how YOU want your daily briefs. These directives are injected into every brief from now on,
            and they override Hidro's defaults when they conflict. Be specific — Hidro reads this every morning.
          </p>

          <label className="block mt-4" htmlFor="brief-coaching-text">
            <span className="block text-[11px] uppercase tracking-[0.18em] text-[#647087] mb-1.5">Your standing instructions</span>
            <DictTextarea
              id="brief-coaching-text"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={8}
              maxLength={2000}
              placeholder={`e.g.\n• Keep every brief under 6 bullets — no fluff.\n• Lead with tomorrow's call list, then today's recap last.\n• Stop using emojis in section headings.\n• Always quote my real call count vs my target, even if it's bad.\n• Talk to me like a sharp mentor, not a corporate newsletter.`}
              className="hv-input text-sm w-full leading-relaxed"
              data-testid="brief-coaching-textarea"
            />
            <div className="flex items-center justify-between mt-1.5 text-[11px] text-[#647087]">
              <span>{instructions.length}/2000 characters</span>
              {updatedAt && <span>Last saved: {new Date(updatedAt).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg" })}</span>}
            </div>
          </label>

          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={save}
              disabled={!dirty || busy}
              className="hv-btn hv-btn-primary text-sm disabled:opacity-50"
              data-testid="brief-coaching-save"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save & apply to next brief
            </button>
            {dirty && (
              <button
                type="button"
                onClick={() => setInstructions(initial)}
                className="hv-btn hv-btn-ghost text-sm"
                data-testid="brief-coaching-discard"
              >
                Discard changes
              </button>
            )}
            {savedAt > 0 && Date.now() - savedAt < 4000 && (
              <span className="text-emerald-300 text-xs"><CheckCircle2 className="w-3 h-3 inline mr-1" /> Saved.</span>
            )}
          </div>

          {/* Recent feedback log */}
          <div className="mt-6 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div>
                <h3 className="font-display text-base font-bold">What Hidro has heard from you</h3>
                <p className="text-[11px] text-[#647087] mt-0.5">
                  Recent thumbs-up / thumbs-down you've left on briefs. Hidro weights these in addition to your standing instructions. Delete a row if it no longer applies.
                </p>
              </div>
            </div>
            {recent.length === 0 ? (
              <div className="text-xs text-[#647087] py-3" data-testid="brief-coaching-empty">
                No feedback yet. Open a brief in <a className="text-[#1FB6C1] underline" href="/inbox">/inbox</a> and tap "Coach Hidro" to start training him.
              </div>
            ) : (
              <ul className="space-y-1.5" data-testid="brief-coaching-feedback-list">
                {recent.map((r) => (
                  <li key={r.id} className="flex items-start justify-between gap-3 p-2 rounded border border-white/5 bg-white/[0.02]">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      {r.rating === "up" ? (
                        <ThumbsUp className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" />
                      ) : (
                        <ThumbsDown className="w-3.5 h-3.5 text-red-300 mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        {r.comment ? (
                          <div className="text-[12px] text-[#d7dde8] leading-snug break-words">"{r.comment}"</div>
                        ) : (
                          <div className="text-[12px] text-[#647087] italic">No comment — {r.rating === "up" ? "kept tone" : "asked for a rewrite"}.</div>
                        )}
                        <div className="text-[10px] text-[#647087] mt-0.5">
                          {r.mode === "recap" ? "evening recap" : "morning brief"} · {new Date(r.created_at).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg" })}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteFeedback(r.id)}
                      className="text-[#647087] hover:text-red-300 shrink-0"
                      data-testid={`brief-coaching-feedback-delete-${r.id}`}
                      title="Forget this feedback"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



/* ---------------- Feature-flag kill-switch (owner/admin only) ----------------
   Lets the owner instantly disable a refactored route module across the
   pod fleet without touching the shell. Persists to Mongo via
   PUT /api/admin/feature-flags/{name}. */
function FeatureFlagsCard() {
  const { user } = useAuth();
  const [flags, setFlags] = useState(null);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  // Categories collapsed by default — Owner can drill into the one they want.
  const [openCats, setOpenCats] = useState({});
  const isOwner = user?.role === "owner" || user?.role === "admin";

  const refresh = async () => {
    const { data } = await api.get("/admin/feature-flags");
    setFlags(data.flags || []);
    setCategories(data.categories || []);
  };

  useEffect(() => {
    if (!isOwner) return;
    refresh().catch(() => setFlags([]));
  }, [isOwner]);

  if (!isOwner) return null;

  const toggle = async (name, next) => {
    setBusy(true);
    try {
      await api.put(`/admin/feature-flags/${name}`, { enabled: next });
      await refresh();
    } catch (_e) { /* noop */ } finally { setBusy(false); }
  };
  const reset = async (name) => {
    setBusy(true);
    try {
      await api.delete(`/admin/feature-flags/${name}`);
      await refresh();
    } catch (_e) { /* noop */ } finally { setBusy(false); }
  };

  // Group flags by category, preserving the canonical category order from
  // the API response. Anything missing falls into "Other".
  const grouped = (categories.length ? categories : ["Other"]).map((cat) => ({
    cat,
    items: (flags || []).filter((f) => (f.category || "Other") === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="hv-card p-5 border-amber-500/30" data-testid="feature-flags-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0">
          <Shield className="w-5 h-5 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-display text-2xl font-bold">Feature flags</h2>
            <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300 border border-amber-500/40 rounded px-2 py-0.5">Owner only</span>
          </div>
          <p className="text-sm text-[#a0aabf] mt-1">
            Emergency kill-switch for any refactored route module. Toggling off makes that resource's endpoints return 404 across the fleet —
            <strong> a backend restart is still required for the change to take effect on already-mounted handlers.</strong> Future deploys honour the override automatically.
          </p>
          {!flags ? (
            <div className="text-xs text-[#647087] mt-3"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading…</div>
          ) : flags.length === 0 ? (
            <div className="text-xs text-[#647087] mt-3">No modules registered.</div>
          ) : (
            <div className="mt-3 space-y-2">
              {grouped.map(({ cat, items }) => {
                const open = openCats[cat] ?? false;
                const offCount = items.filter((f) => !f.effective).length;
                return (
                  <div key={cat} className="border border-[#1f2940] rounded-md overflow-hidden" data-testid={`feature-flags-cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                    <button
                      type="button"
                      onClick={() => setOpenCats((s) => ({ ...s, [cat]: !open }))}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-[#0c1424] hover:bg-[#0e1830] transition-colors"
                      data-testid={`feature-flags-cat-toggle-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-xs transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
                        <span className="text-sm font-medium truncate">{cat}</span>
                        <span className="text-[10px] text-[#647087] tabular-nums">{items.length} module{items.length === 1 ? "" : "s"}</span>
                      </div>
                      {offCount > 0 && (
                        <span className="text-[10px] uppercase tracking-[0.15em] text-rose-300 border border-rose-500/40 rounded px-1.5 py-0.5 shrink-0">{offCount} OFF</span>
                      )}
                    </button>
                    {open && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-2 bg-[#070c18]">
                        {items.map((f) => (
                          <div
                            key={f.name}
                            className={`border rounded-md p-2.5 flex items-center justify-between gap-3 ${f.effective ? "border-emerald-500/30 bg-emerald-500/[0.04]" : "border-rose-500/40 bg-rose-500/[0.06]"}`}
                            data-testid={`feature-flag-row-${f.name}`}
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{f.name}</div>
                              <div className="text-[10px] text-[#647087] truncate">
                                {f.mongo !== null && f.mongo !== undefined ? <span className="text-amber-300">override: {String(f.mongo)}</span> : f.env !== null && f.env !== undefined ? <span>env: {String(f.env)}</span> : <span>default ON</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {f.mongo !== null && f.mongo !== undefined && (
                                <button type="button" disabled={busy} onClick={() => reset(f.name)} className="hv-btn hv-btn-ghost text-[10px] px-1.5 py-0.5" data-testid={`feature-flag-reset-${f.name}`} title="Remove override (back to env/default)">
                                  Reset
                                </button>
                              )}
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => toggle(f.name, !f.effective)}
                                className={`hv-btn text-[10px] px-2 py-1 ${f.effective ? "hv-btn-ghost" : "border-rose-400/60 text-rose-300"}`}
                                data-testid={`feature-flag-toggle-${f.name}`}
                              >
                                {f.effective ? "ON" : "OFF"}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



/* ---------------- Triage console (owner/admin only) ----------------
   Polls /api/admin/system-metrics every 5 s and surfaces latency, error
   rate, slowest endpoints and most-erroring endpoints over the last 5 min.
   Helps spot regressions live without leaving the app. */
function TriageConsoleCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [windowSec, setWindowSec] = useState(300);
  const [paused, setPaused] = useState(false);
  const [fleet, setFleet] = useState(false);
  const [autopsy, setAutopsy] = useState(null); // { endpoint, loading, payload }
  const isOwner = user?.role === "owner" || user?.role === "admin";

  useEffect(() => {
    if (!isOwner) return;
    let mounted = true;
    const tick = async () => {
      if (paused) return;
      try {
        const { data } = await api.get(`/admin/system-metrics?window=${windowSec}&fleet=${fleet ? "true" : "false"}`);
        if (mounted) setData(data);
      } catch (_e) { /* noop */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { mounted = false; clearInterval(id); };
  }, [isOwner, windowSec, paused, fleet]);

  // If we just toggled into per-pod and the window is too long, reset.
  useEffect(() => {
    if (!fleet && windowSec > 3600) setWindowSec(300);
  }, [fleet, windowSec]);

  if (!isOwner) return null;

  const errPct = data?.error_rate_pct ?? 0;
  const errTone = errPct >= 5 ? "rose" : errPct >= 1 ? "amber" : "emerald";
  const p95 = data?.p95_latency_ms ?? 0;
  const p95Tone = p95 >= 1500 ? "rose" : p95 >= 500 ? "amber" : "emerald";

  // Fleet mode unlocks longer windows (up to 24h thanks to TTL retention)
  const windows = fleet ? [300, 900, 3600, 14400, 86400] : [60, 300, 900, 3600];

  const labelForWindow = (s) => {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${s / 60}m`;
    if (s < 86400) return `${s / 3600}h`;
    return "24h";
  };

  return (
    <div className="hv-card p-5 border-cyan-500/30" data-testid="triage-console-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center shrink-0">
          <Activity className="w-5 h-5 text-cyan-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-display text-2xl font-bold">Triage console</h2>
            <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-300 border border-cyan-500/40 rounded px-2 py-0.5">
              {fleet ? "Fleet · Mongo" : "This pod · live"}
            </span>
          </div>
          <p className="text-sm text-[#a0aabf] mt-1">
            {fleet
              ? "Cross-replica request log persisted to MongoDB (24 h TTL retention). Refreshes every 5 s."
              : `Live request latency & error telemetry from this pod's in-memory ring buffer (last ${data?.buffer_used ?? 0}/${data?.buffer_capacity ?? 1024} requests). Refreshes every 5 s.`}
          </p>

          {/* Mode toggle: per-pod vs fleet */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button
              type="button"
              onClick={() => setFleet(false)}
              className={`hv-btn text-[10px] px-2 py-1 ${!fleet ? "" : "hv-btn-ghost"}`}
              data-testid="triage-mode-pod"
            >
              This pod
            </button>
            <button
              type="button"
              onClick={() => setFleet(true)}
              className={`hv-btn text-[10px] px-2 py-1 ${fleet ? "" : "hv-btn-ghost"}`}
              data-testid="triage-mode-fleet"
            >
              Fleet (24h)
            </button>
            <span className="w-px h-5 bg-[#1f2940] mx-1" />
            {windows.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setWindowSec(s)}
                className={`hv-btn text-[10px] px-2 py-1 ${windowSec === s ? "" : "hv-btn-ghost"}`}
                data-testid={`triage-window-${s}`}
              >
                {labelForWindow(s)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              className={`hv-btn text-[10px] px-2 py-1 ${paused ? "border-amber-400/60 text-amber-300" : "hv-btn-ghost"}`}
              data-testid="triage-pause-toggle"
            >
              {paused ? "Resume" : "Pause"}
            </button>
            <span className="text-[10px] text-[#647087] ml-auto" data-testid="triage-req-count">
              {data?.request_count ?? 0} requests in window
            </span>
          </div>

          {/* Headline KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            <KpiPill label="Avg latency" value={`${data?.avg_latency_ms ?? 0} ms`} tone="cyan" testid="triage-kpi-avg" />
            <KpiPill label="p50" value={`${data?.p50_latency_ms ?? 0} ms`} tone="cyan" testid="triage-kpi-p50" />
            <KpiPill label="p95" value={`${p95} ms`} tone={p95Tone} testid="triage-kpi-p95" />
            <KpiPill label="Errors" value={`${errPct}% (${data?.error_count ?? 0})`} tone={errTone} testid="triage-kpi-errors" />
          </div>

          {/* Slowest endpoints */}
          {data?.top_slow_endpoints?.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#a0aabf] mb-1.5">
                Slowest endpoints {fleet && <span className="text-cyan-300 normal-case tracking-normal">· click for autopsy</span>}
              </div>
              <div className="space-y-1">
                {data.top_slow_endpoints.slice(0, 5).map((ep, i) => {
                  const clickable = fleet; // autopsy reads from request_log (fleet only)
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={!clickable}
                      onClick={() => clickable && openAutopsy(ep.endpoint)}
                      className={`w-full flex items-center justify-between gap-2 text-xs px-2 py-1 rounded border border-[#1f2940] bg-[#0c1424] text-left ${clickable ? "hover:border-cyan-500/40 hover:bg-[#0e1830] cursor-pointer transition-colors" : "cursor-default"}`}
                      data-testid={`triage-slow-${i}`}
                    >
                      <span className="truncate font-mono text-[#cbd5e1]">{ep.endpoint}</span>
                      <span className="shrink-0 text-[#a0aabf] tabular-nums">{ep.avg_latency_ms} ms avg · max {ep.max_latency_ms} · {ep.count}×</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top erroring endpoints */}
          {data?.top_error_endpoints?.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-[0.2em] text-rose-300 mb-1.5">Top erroring endpoints</div>
              <div className="space-y-1">
                {data.top_error_endpoints.slice(0, 5).map((ep, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs px-2 py-1 rounded border border-rose-500/30 bg-rose-500/[0.06]" data-testid={`triage-error-${i}`}>
                    <span className="truncate font-mono text-[#fecaca]">{ep.endpoint}</span>
                    <span className="shrink-0 text-rose-200 tabular-nums">{ep.errors} err · {ep.count}×</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Live tail — last 30 requests, newest at top */}
          {data?.recent?.length > 0 && (
            <div className="mt-4" data-testid="triage-live-tail">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#a0aabf] mb-1.5">
                Live tail · last {data.recent.length} requests
              </div>
              <div className="max-h-56 overflow-auto rounded border border-[#1f2940] bg-[#070c18] divide-y divide-[#101828]">
                {data.recent.map((r, i) => {
                  const status = Number(r.status || 0);
                  const tone =
                    status >= 500 ? "text-rose-300 bg-rose-500/[0.06]" :
                    status >= 400 ? "text-amber-300 bg-amber-500/[0.05]" :
                    "text-emerald-300";
                  const lat = Number(r.latency_ms || 0);
                  const latTone = lat >= 1500 ? "text-rose-300" : lat >= 500 ? "text-amber-300" : "text-[#a0aabf]";
                  return (
                    <div key={i} className={`flex items-center gap-2 px-2 py-1 text-[11px] font-mono tabular-nums ${tone}`} data-testid={`triage-tail-row-${i}`}>
                      <span className="w-10 shrink-0 text-center">{r.method}</span>
                      <span className="w-12 shrink-0 text-center">{status}</span>
                      <span className={`w-16 shrink-0 text-right ${latTone}`}>{Math.round(lat)} ms</span>
                      <span className="truncate flex-1 text-[#cbd5e1]">{r.path}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {data && data.request_count === 0 && (
            <div className="text-xs text-[#647087] mt-3 italic">No traffic recorded in the last {windowSec}s.</div>
          )}

          {/* Slow query autopsy drawer (inline) */}
          {autopsy && (
            <AutopsyPanel
              autopsy={autopsy}
              onClose={() => setAutopsy(null)}
            />
          )}
        </div>
      </div>
    </div>
  );

  // Helper: fetch the autopsy payload for a "METHOD /path" string.
  function openAutopsy(endpointLabel) {
    const sp = endpointLabel.indexOf(" ");
    const method = endpointLabel.slice(0, sp);
    const path = endpointLabel.slice(sp + 1);
    setAutopsy({ endpoint: endpointLabel, loading: true, payload: null });
    api
      .get(`/admin/system-metrics/endpoint-history?path=${encodeURIComponent(path)}&method=${encodeURIComponent(method)}&window=${Math.max(windowSec, 3600)}&limit=50`)
      .then(({ data }) => setAutopsy({ endpoint: endpointLabel, loading: false, payload: data }))
      .catch(() => setAutopsy({ endpoint: endpointLabel, loading: false, payload: null, error: true }));
  }
}

function AutopsyPanel({ autopsy, onClose }) {
  const p = autopsy.payload;
  // Sparkline geometry
  const W = 600, H = 60, PAD = 4;
  const rows = p?.rows || [];
  const maxLat = Math.max(1, ...rows.map((r) => Number(r.latency_ms || 0)));
  const stepX = rows.length > 1 ? (W - PAD * 2) / (rows.length - 1) : 0;

  return (
    <div className="mt-4 border border-cyan-500/40 rounded-md bg-[#070c18] overflow-hidden" data-testid="triage-autopsy-panel">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-cyan-500/[0.06] border-b border-cyan-500/30">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-300">Slow query autopsy</div>
          <div className="text-sm font-mono truncate" data-testid="triage-autopsy-endpoint">{autopsy.endpoint}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="hv-btn hv-btn-ghost text-[10px] px-2 py-1"
          data-testid="triage-autopsy-close"
        >
          Close
        </button>
      </div>
      <div className="p-3">
        {autopsy.loading ? (
          <div className="text-xs text-[#647087]"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading hits…</div>
        ) : autopsy.error || !p ? (
          <div className="text-xs text-rose-300">Failed to load endpoint history.</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-[#647087]">No requests recorded for this endpoint in the last {Math.round((p.window_seconds || 0) / 60)}m.</div>
        ) : (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
              <KpiPill label="Hits" value={p.count} tone="cyan" testid="autopsy-kpi-count" />
              <KpiPill label="Avg" value={`${p.avg_latency_ms} ms`} tone="cyan" testid="autopsy-kpi-avg" />
              <KpiPill label="p50" value={`${p.p50_latency_ms} ms`} tone="cyan" testid="autopsy-kpi-p50" />
              <KpiPill label="p95" value={`${p.p95_latency_ms} ms`} tone={p.p95_latency_ms >= 1500 ? "rose" : p.p95_latency_ms >= 500 ? "amber" : "emerald"} testid="autopsy-kpi-p95" />
              <KpiPill label="Max" value={`${p.max_latency_ms} ms`} tone="amber" testid="autopsy-kpi-max" />
            </div>
            {/* Sparkline */}
            <div className="border border-[#1f2940] rounded bg-[#0c1424] p-2" data-testid="autopsy-sparkline">
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14">
                <polyline
                  fill="none"
                  stroke="#22d3ee"
                  strokeWidth="1.5"
                  points={rows.map((r, i) => {
                    const x = PAD + i * stepX;
                    const y = H - PAD - ((Number(r.latency_ms || 0) / maxLat) * (H - PAD * 2));
                    return `${x},${y}`;
                  }).join(" ")}
                />
                {rows.map((r, i) => {
                  const x = PAD + i * stepX;
                  const y = H - PAD - ((Number(r.latency_ms || 0) / maxLat) * (H - PAD * 2));
                  const status = Number(r.status || 0);
                  const fill = status >= 500 ? "#fb7185" : status >= 400 ? "#fbbf24" : "#22d3ee";
                  return <circle key={i} cx={x} cy={y} r="1.8" fill={fill} />;
                })}
              </svg>
              <div className="flex items-center justify-between text-[10px] text-[#647087] mt-1 px-1">
                <span>{rows.length > 0 && rows[0].ts_iso ? new Date(rows[0].ts_iso).toLocaleString() : ""}</span>
                <span>now</span>
              </div>
            </div>
            {/* Recent rows */}
            <div className="mt-3 max-h-48 overflow-auto rounded border border-[#1f2940] bg-[#070c18] divide-y divide-[#101828]">
              {rows.slice(-20).reverse().map((r, i) => {
                const status = Number(r.status || 0);
                const tone =
                  status >= 500 ? "text-rose-300 bg-rose-500/[0.06]" :
                  status >= 400 ? "text-amber-300 bg-amber-500/[0.05]" :
                  "text-[#cbd5e1]";
                const lat = Number(r.latency_ms || 0);
                return (
                  <div key={i} className={`flex items-center gap-2 px-2 py-1 text-[11px] font-mono tabular-nums ${tone}`} data-testid={`autopsy-row-${i}`}>
                    <span className="w-12 shrink-0 text-center">{status}</span>
                    <span className="w-16 shrink-0 text-right">{Math.round(lat)} ms</span>
                    <span className="truncate flex-1 text-[#647087] text-[10px]">{r.ts_iso ? new Date(r.ts_iso).toLocaleTimeString() : ""}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function KpiPill({ label, value, tone = "cyan", testid }) {
  const toneClass = {
    cyan: "border-cyan-500/30 bg-cyan-500/[0.05]",
    emerald: "border-emerald-500/30 bg-emerald-500/[0.05]",
    amber: "border-amber-500/40 bg-amber-500/[0.06]",
    rose: "border-rose-500/40 bg-rose-500/[0.07]",
  }[tone] || "";
  return (
    <div className={`rounded-md border px-3 py-2 ${toneClass}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0aabf]">{label}</div>
      <div className="text-base font-display font-bold tabular-nums">{value}</div>
    </div>
  );
}

/**
 * Iter 290zp.7 — Cross-tenant chat-member cleanup (OWNER ONLY).
 *
 * Reusable maintenance tool. We expose it permanently because:
 *   • The original leak (demo seed users appearing in JC chats) was
 *     fixed in the code path, but legacy data needed a one-shot scrub.
 *   • If a future code change introduces a similar cross-tenant
 *     contamination, the owner can self-heal without an Emergent
 *     Support ticket. Dry-run report first, commit second — never
 *     destructive on the first click.
 *
 * Backend: POST /api/chats/maintenance/cleanup-cross-tenant-members
 * Auth:    Owner only (enforced both client- and server-side).
 */
function ChatMemberCleanupCard() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [scanning, setScanning] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState(false);

  if (!isOwner) return null;

  const scan = async () => {
    setScanning(true);
    setReport(null);
    try {
      const { data } = await api.post(
        "/chats/maintenance/cleanup-cross-tenant-members?commit=false",
      );
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed.");
    } finally {
      setScanning(false);
    }
  };

  const commit = async () => {
    if (!report || report.threads_with_leaks === 0) return;
    if (!window.confirm(
      `Strip ${report.threads_with_leaks} cross-tenant member references from your chat threads? This cannot be undone (legacy IDs will be removed from the member_ids arrays). The chat history itself is preserved.`,
    )) return;
    setCommitting(true);
    try {
      const { data } = await api.post(
        "/chats/maintenance/cleanup-cross-tenant-members?commit=true",
      );
      setReport(data);
      toast.success(`Cleaned ${data.threads_fixed} chat threads.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed.");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
      data-testid="settings-chat-member-cleanup-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-white text-[14px]">Chat member integrity check</h3>
          <p className="text-[11px] text-[#647087] mt-1 leading-relaxed">
            Scans every chat thread in your tenant for member references that don&apos;t belong to a user in
            <strong className="text-[#a0aabf]"> your tenant</strong>. Reports first, then lets you strip the foreign IDs.
            Run this if you spot unfamiliar names in JC / ticket chats.
          </p>
        </div>
        <span className="text-[9px] uppercase tracking-wider px-2 py-1 rounded bg-amber-500/10 border border-amber-400/30 text-amber-300 shrink-0">
          Owner only
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={scan}
          disabled={scanning || committing}
          data-testid="settings-chat-cleanup-scan"
          className="text-[12px] px-3 py-1.5 rounded border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
          {scanning ? "Scanning…" : "Scan for cross-tenant leaks"}
        </button>
        {report && report.threads_with_leaks > 0 && (
          <button
            type="button"
            onClick={commit}
            disabled={committing || scanning}
            data-testid="settings-chat-cleanup-commit"
            className="text-[12px] px-3 py-1.5 rounded border border-rose-400/40 bg-rose-500/[0.10] hover:bg-rose-500/[0.20] text-rose-100 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            {committing ? "Cleaning…" : `Clean ${report.threads_with_leaks} threads`}
          </button>
        )}
      </div>

      {report && (
        <div
          className="mt-4 rounded border border-white/5 bg-black/30 p-3 text-[11px] font-mono"
          data-testid="settings-chat-cleanup-report"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <Stat label="Tenant" value={report.tenant_id} accent="#a0aabf" />
            <Stat label="Legit users" value={report.legit_user_count} accent="#10b981" />
            <Stat label="Threads scanned" value={report.threads_scanned} accent="#94a3b8" />
            <Stat
              label="Threads with leaks"
              value={report.threads_with_leaks}
              accent={report.threads_with_leaks > 0 ? (report.committed ? "#10b981" : "#f59e0b") : "#10b981"}
            />
          </div>
          {report.committed && (
            <div className="text-emerald-300 mb-2">
              ✓ Committed — {report.threads_fixed} thread{report.threads_fixed === 1 ? "" : "s"} fixed.
            </div>
          )}
          {report.threads_with_leaks > 0 && (
            <>
              <div className="text-[#a0aabf] mb-1">Leaked user IDs (top by thread count):</div>
              <ul className="space-y-0.5 mb-2">
                {(report.leaked_user_ids || []).slice(0, 10).map((u) => (
                  <li key={u.id} className="flex items-center gap-2 text-[#cbd5e1]">
                    <span className="text-rose-400">▸</span>
                    <code className="text-[10px]">{u.id}</code>
                    <span className="text-[#647087]">×{u.thread_count}</span>
                  </li>
                ))}
                {(report.leaked_user_ids || []).length > 10 && (
                  <li className="text-[#647087] text-[10px]">
                    + {report.leaked_user_ids.length - 10} more…
                  </li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-[10px] uppercase tracking-wider text-cyan-300 hover:text-cyan-100"
              >
                {expanded ? "Hide" : "Show"} sample affected threads
              </button>
              {expanded && (
                <div className="mt-2 max-h-48 overflow-y-auto space-y-1 border-t border-white/5 pt-2">
                  {(report.sample_threads || []).map((t) => (
                    <div key={t.thread_id} className="text-[10px] text-[#94a3b8]">
                      <span className="text-cyan-400">{t.kind}</span> · {t.title || t.entity_id?.slice(0, 8)}
                      <span className="text-[#647087] ml-1">({t.leaked_ids?.length || 0} bad ids)</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {report.threads_with_leaks === 0 && (
            <div className="text-emerald-300">✓ All chat threads are clean. No action needed.</div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[#647087] mb-0.5">{label}</div>
      <div className="text-[14px] font-bold" style={{ color: accent }}>{value}</div>
    </div>
  );
}

export {
  ResetPasswordModal,
  EditUserModal,
  DataMigrationBlock,
  PasswordResetRequestsBlock,
  SessionLengthCard,
  DeviceAppCard,
  AssetManagerBlock,
  ThemeTogglePanel,
  PhoneNormaliserBlock,
  MenuCustomisationCard,
  HidroPreferencesCard,
  BriefCoachingCard,
  FeatureFlagsCard,
  TriageConsoleCard,
  ChatMemberCleanupCard,
};
